import { useMemo } from 'react';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useFlowCardExplorerContext } from './FlowCardExplorerContext';
import { useFlowTokensByOwnerUri } from '../../../store/flow-tokens/useFlowTokens';

export function ColumnTypeDevice(props) {
  const { i18n } = useI18n();

  const { sections } = useDeviceSections({
    i18n,
    entry: props.entry,
    columnProps: props.columnProps,
  });

  return (
    <props.listBoxComponent {...props.listBoxProps} aria-label="Device column" items={sections} />
  );
}

function useDeviceSections({ i18n, entry, columnProps }) {
  const { cardType, cardFilter, onCardSelect } = columnProps;
  const cardTypePlural = `${cardType}s`;
  const flowCardExplorerContext = useFlowCardExplorerContext();
  const cards = flowCardExplorerContext[cardTypePlural];

  const flowTokens = useFlowTokensByOwnerUri();

  const device = useMemo(() => {
    return cards.byDeviceId[entry.key] ?? {};
  }, [cards.byDeviceId, entry.key]);

  // For conditions cards we show all logic cards prefilled for every device number token.
  const logicCards = useMemo(() => {
    if (cardType !== 'condition') {
      return [];
    }

    const conditions = flowCardExplorerContext.conditions;
    const cards = conditions.byManagerUri['homey:manager:logic']?.cardsByKey;
    const deviceTokens = flowTokens.byOwnerUri?.[`homey:device:${entry.key}`];

    const allCards = [];

    for (const [tokenKey, token] of Object.entries(deviceTokens ?? {})) {
      if (token.type !== 'number') {
        continue;
      }

      for (const [cardKey, card] of Object.entries(cards)) {
        if (card.droptoken === 'number' || card.droptoken?.includes('number')) {
          const cardItem = {
            key: `${cardKey}::${tokenKey}`,
            type: 'card',
            textValue: card.title ?? card.titleFormatted,
            action() {
              onCardSelect(cardKey, card, `${token.ownerUri}|${token.ownerId}`);
            },
            context: {
              card,
              droptoken: {
                ...token,
              },
            },
          };

          allCards.push(cardItem);
        }
      }
    }

    return allCards;
  }, [
    entry.key,
    cardType,
    flowTokens.byOwnerUri,
    flowCardExplorerContext.conditions,
    onCardSelect,
  ]);

  const sections = useMemo(() => {
    const sections = [];

    const cardItems = [];
    const cardsByKeyEntries = Object.entries(device.cardsByKey ?? {});

    for (const [cardKey, card] of cardsByKeyEntries) {
      if (cardFilter(card) === false) continue;

      const cardItem = {
        key: cardKey,
        type: 'card',
        textValue: card.title ?? card.titleFormatted,
        action() {
          onCardSelect(cardKey, card);
        },
        context: {
          card,
        },
      };

      // If the card is popular, add it to the start of the array.
      if (card.highlight === true) {
        cardItems.unshift(cardItem);
        continue;
      }

      cardItems.push(cardItem);
    }

    if (cardItems.length > 0) {
      sections.push({
        key: 'cards',
        title: i18n.messageFormatter('flow.cardDialog.cards'),
        children: cardItems,
      });
    }

    if (logicCards.length > 0) {
      sections.push({
        key: 'logic',
        title: i18n.messageFormatter('manager.logic.name'),
        children: logicCards,
      });
    }

    return sections;
  }, [i18n, cardFilter, onCardSelect, device, logicCards]);

  return { sections };
}
